import { brand, neutral } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import React, { ReactNode } from "react";
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";
import ThemedText from "./ThemedText";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
type AvatarVariant = "circular" | "rounded" | "square";
type StatusType = "online" | "offline" | "away" | "busy" | "none";

interface AvatarProps {
  name?: string;
  src?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  icon?: ReactNode;
  backgroundColor?: string;
  textColor?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  badge?: ReactNode;
  alt?: string;
  status?: StatusType;
  showStatusBorder?: boolean;
}

export default function Avatar({
  name,
  src,
  size = "md",
  variant = "circular",
  icon,
  backgroundColor,
  textColor,
  style,
  textStyle,
  badge,
  alt,
  status = "none",
  showStatusBorder = true,
}: AvatarProps) {
  const isDark = useThemeColor({}, "background") === "#151718";

  const sizeStyles: Record<AvatarSize, { size: number; fontSize: number }> = {
    xs: { size: 24, fontSize: 10 },
    sm: { size: 32, fontSize: 12 },
    md: { size: 40, fontSize: 16 },
    lg: { size: 48, fontSize: 18 },
    xl: { size: 64, fontSize: 24 },
    "2xl": { size: 96, fontSize: 36 },
  };

  const getBorderRadius = () => {
    const avatarSize = sizeStyles[size].size;
    switch (variant) {
      case "circular":
        return avatarSize / 2;
      case "rounded":
        return avatarSize / 4;
      case "square":
        return 0;
    }
  };

  const getInitials = (fullName: string): string => {
    if (!fullName) return "?";

    const names = fullName.trim().split(" ");
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }

    return (
      names[0].charAt(0).toUpperCase() +
      names[names.length - 1].charAt(0).toUpperCase()
    );
  };

  const getBackgroundColor = (name?: string): string => {
    if (backgroundColor) return backgroundColor;

    if (!name) return isDark ? neutral[700] : neutral[300];

    // Generate consistent color based on name
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E2",
      "#F8B500",
      "#52B788",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const getTextColor = (): string => {
    if (textColor) return textColor;
    return "#FFFFFF";
  };

  const getStatusColor = (): string => {
    switch (status) {
      case "online":
        return "#22C55E"; // Green
      case "offline":
        return "#94A3B8"; // Gray
      case "away":
        return "#F59E0B"; // Orange
      case "busy":
        return "#EF4444"; // Red
      default:
        return "transparent";
    }
  };

  const getStatusSize = (): number => {
    const avatarSize = sizeStyles[size].size;
    // Status indicator is approximately 25% of avatar size
    return Math.max(avatarSize * 0.25, 8);
  };

  const renderContent = () => {
    // Priority: Image > Icon > Initials
    if (src) {
      return (
        <Image
          source={{ uri: src }}
          style={[
            {
              width: sizeStyles[size].size,
              height: sizeStyles[size].size,
              borderRadius: getBorderRadius(),
            },
          ]}
          contentFit="cover"
          placeholder={alt}
        />
      );
    }

    if (icon) {
      return <View style={styles.iconContainer}>{icon}</View>;
    }

    if (name) {
      return (
        <ThemedText
          style={[
            {
              fontSize: sizeStyles[size].fontSize,
              fontFamily: fonts.bold,
              color: getTextColor(),
            },
            textStyle,
          ]}
        >
          {getInitials(name)}
        </ThemedText>
      );
    }

    // Fallback to default icon
    return (
      <Ionicons
        name="person"
        size={sizeStyles[size].size * 0.5}
        color={getTextColor()}
      />
    );
  };

  return (
    <View style={[styles.container, style]}>
      <View
        style={[
          styles.avatar,
          {
            width: sizeStyles[size].size,
            height: sizeStyles[size].size,
            borderRadius: getBorderRadius(),
            backgroundColor: getBackgroundColor(name),
          },
        ]}
      >
        {renderContent()}
      </View>

      {badge && <View style={styles.badge}>{badge}</View>}

      {/* Status Indicator */}
      {status !== "none" && (
        <View
          style={[
            styles.statusIndicator,
            {
              width: getStatusSize(),
              height: getStatusSize(),
              borderRadius: getStatusSize() / 2,
              backgroundColor: getStatusColor(),
              borderWidth: showStatusBorder ? 2 : 0,
              borderColor: isDark ? "#151718" : "#FFFFFF",
            },
          ]}
        />
      )}
    </View>
  );
}

// Avatar Group Component
interface AvatarGroupProps {
  children: ReactNode;
  max?: number;
  spacing?: number;
  style?: StyleProp<ViewStyle>;
}

export function AvatarGroup({
  children,
  max = 5,
  spacing = -8,
  style,
}: AvatarGroupProps) {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;

  return (
    <View style={[styles.groupContainer, style]}>
      {visibleChildren.map((child, index) => (
        <View
          key={index}
          style={[
            styles.groupItem,
            {
              marginLeft: index > 0 ? spacing : 0,
              zIndex: visibleChildren.length - index,
            },
          ]}
        >
          {child}
        </View>
      ))}

      {remainingCount > 0 && (
        <View
          style={[
            styles.groupItem,
            {
              marginLeft: spacing,
              zIndex: 0,
            },
          ]}
        >
          <Avatar name={`+${remainingCount}`} size="md" />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
  },
  avatar: {
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  iconContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  badge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 1,
  },
  statusIndicator: {
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 2,
  },
  groupContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  groupItem: {
    borderWidth: 2,
    borderColor: "#FFFFFF",
    borderRadius: 100,
  },
});
